import { createContext, useContext, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { accessTokenSet, authReady, sessionCleared, sessionSet } from "../features/auth/authSlice";
import { apiService } from "../services/apiService";

const AuthContext = createContext(null);
export function AuthProvider({ children }) {
  const dispatch = useDispatch();
  const session = useSelector((state) => state.auth);
  const signIn = useCallback((data) => dispatch(sessionSet({ user: data.user, accessToken: data.accessToken })), [dispatch]);
  const clearSession = useCallback(() => dispatch(sessionCleared()), [dispatch]);
  useEffect(() => {
    const syncSession = (event) => {
      if (event.detail?.accessToken) dispatch(sessionSet(event.detail));
      else dispatch(sessionCleared());
    };
    window.addEventListener("formula-session-updated", syncSession);
    return () => window.removeEventListener("formula-session-updated", syncSession);
  }, [dispatch]);
  useEffect(() => {
    let active = true;
    (async () => {
      if (!session.accessToken) {
        if (active) dispatch(authReady());
        return;
      }
      try {
        const { data } = await apiService.get("/auth/me");
        if (active) dispatch(sessionSet({ user: data.user, accessToken: JSON.parse(localStorage.getItem("formula_session") || "null")?.accessToken || session.accessToken }));
      } catch {
        try {
          const { data } = await apiService.post("/auth/refresh");
          if (active) dispatch(accessTokenSet(data.accessToken));
        } catch {
          if (active) clearSession();
        }
      } finally {
        if (active) dispatch(authReady());
      }
    })();
    return () => { active = false; };
  }, [session.accessToken, dispatch, clearSession]);
  const signOut = useCallback(() => {
    clearSession();
    apiService.post("/auth/logout").catch(() => { /* Local sign-out is intentionally authoritative. */ });
  }, [clearSession]);
  const value = useMemo(() => ({ ...session, isAuthenticated: Boolean(session.accessToken), signIn, signOut }), [session, signIn, signOut]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
}
